import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { DomainResult } from '../../types/index';

interface GenericAssessmentDetailProps {
  assessmentData?: DomainResult;
  title: string;
  filterQuestion?: string;
  testItems: Array<{
    name: string;
    description: string;
  }>;
}

const GenericAssessmentDetail: React.FC<GenericAssessmentDetailProps> = ({ 
  assessmentData, 
  title, 
  filterQuestion, 
  testItems 
}) => {
  const navigate = useNavigate();

  // 添加调试信息（开发环境）
  if (process.env.NODE_ENV === 'development') {
    console.log('评估数据详情:', assessmentData);
  }

  if (!assessmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">未找到评估数据</h2>
          <button
            onClick={() => navigate('/results')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回结果页面
          </button>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: 'PASS' | 'FAIL' | 'INCONCLUSIVE') => {
    switch (status) {
      case 'PASS':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'FAIL':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'INCONCLUSIVE':
        return <AlertCircle className="w-6 h-6 text-yellow-600" />;
    }
  };

  const getStatusText = (status: 'PASS' | 'FAIL' | 'INCONCLUSIVE') => {
    switch (status) {
      case 'PASS':
        return '通过';
      case 'FAIL':
        return '未通过';
      case 'INCONCLUSIVE':
        return '不确定';
    }
  };

  const renderAssessmentDetails = () => {
    // 判断是否正常（支持关键健康因素和内在能力的不同状态值）
    const isHealthy = assessmentData.status === 'PASS' || 
                      assessmentData.status === 'OK' || 
                      assessmentData.status === 'SUPPORTED' || 
                      assessmentData.status === 'CONTINENT';
    
    // 如果没有筛查问题，直接显示测试项目
    if (!filterQuestion) {
      return (
        <div className="space-y-6">
          {/* 具体测试项目 */}
          {testItems.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{item.name}</h3>
              <div className="space-y-4">
                <div className={`border-l-4 ${isHealthy ? 'border-green-500' : 'border-red-500'} pl-4`}>
                  <p className="font-medium text-gray-700 mb-2">
                    测试：{item.description}
                  </p>
                  <p className="text-gray-600">
                    测试结果：<span className={`font-medium ${isHealthy ? 'text-green-600' : 'text-red-600'}`}>
                      {isHealthy ? '正常' : '建议进一步评估'}
                    </span>
                  </p>
                </div>
                </div>
            </div>
          ))}
        </div>
      );
    }

    // 如果有筛查问题且未通过，只显示筛查问题
    if (assessmentData.filterQuestionResult === false || 
        assessmentData.completionMethod === 'filter_question' || 
        (assessmentData.status === 'FAIL' && !assessmentData.testResults?.length)) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">筛查问题</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium text-gray-700 mb-2">
                  问题：{filterQuestion}
                </p>
                <p className="text-gray-600">
                  回答：<span className={`font-medium ${assessmentData.filterQuestionResult ? 'text-green-600' : 'text-red-600'}`}>
                    {assessmentData.filterQuestionResult 
                      ? (title === '心理健康' ? '没有' : '正常') 
                      : (title === '心理健康' ? '有' : '有问题')
                    }
                  </span>
                </p>
              </div>
              {!assessmentData.filterQuestionResult && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>评估结论：</strong>由于筛查问题显示存在问题，无需进行详细测试，直接建议寻求进一步的专业评估。
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // 如果有筛查问题且通过了，显示筛查问题和后续测试
    return (
      <div className="space-y-6">
        {/* 筛查问题 */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">筛查问题</h3>
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="font-medium text-gray-700 mb-2">
              问题：{filterQuestion}
            </p>
            <p className="text-gray-600">
              回答：<span className="font-medium text-green-600">
                {title === '心理健康' ? '没有' : '正常'}
              </span>
            </p>
          </div>
        </div>

        {/* 具体测试项目 */}
        {testItems.map((item, index) => (
          <div key={index} className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{item.name}</h3>
            <div className="space-y-4">
              <div className={`border-l-4 ${isHealthy ? 'border-green-500' : 'border-red-500'} pl-4`}>
                <p className="font-medium text-gray-700 mb-2">
                  测试：{item.description}
                </p>
                <p className="text-gray-600">
                  测试结果：<span className={`font-medium ${isHealthy ? 'text-green-600' : 'text-red-600'}`}>
                    {isHealthy ? '正常' : '建议进一步评估'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 头部 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/results')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            返回结果页面
          </button>
        </div>

        {/* 评估概况 */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">{title}评估详情</h1>
            <div className="flex items-center">
              {getStatusIcon(assessmentData.status)}
              <span className={`ml-2 font-semibold ${
                assessmentData.status === 'PASS' ? 'text-green-600' : 
                assessmentData.status === 'FAIL' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {getStatusText(assessmentData.status)}
              </span>
            </div>
          </div>
        </div>

        {/* 评估详情 */}
        {renderAssessmentDetails()}

        {/* 底部操作 */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/results')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回评估结果
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenericAssessmentDetail;